import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { useCurrentTenant } from "store/selectors/hooks";

const HIPAA_ACKNOWLEDGEMENT_TEXT = `This integration may transmit Protected Health Information (PHI) to a third-party service.

Your organization is responsible for:
• Having a Business Associate Agreement with this service, if required
• Configuring this service in compliance with your HIPAA obligations
• Determining whether PHI transmission to this service is appropriate

This action will be logged for compliance purposes.`;

export const useHipaaAcknowledgement = () => {
    const tenant = useCurrentTenant();
    const trpcClient = trpc.useTRPC();
    const acknowledgeMutation = useMutation({
        ...trpcClient.integrations.acknowledgeHipaaRisk.mutationOptions(),
    });

    const requiresAcknowledgement = tenant?.baaEnforced ?? false;

    const confirmHipaaRisk = async (serviceName: string): Promise<boolean> => {
        if (!requiresAcknowledgement) {
            return true;
        }

        const confirmed = await fancyConfirm(
            "Third-Party Integration Notice",
            HIPAA_ACKNOWLEDGEMENT_TEXT,
            "Continue",
            "Cancel",
            {
                confirmStyle: "question",
                extraConfirm: true,
                extraConfirmLabel: "I'll protect PHI appropriately.",
            },
        );

        if (confirmed) {
            await acknowledgeMutation.mutateAsync({ serviceName });
            return true;
        }

        return false;
    };

    return { requiresAcknowledgement, confirmHipaaRisk };
};
